import { toast } from 'react-toastify';
import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Trash2, Plus, Edit3 } from 'lucide-react';
import { parkingFloorService, parkingTypeService, parkingAddonService, parkingSpaceService } from '../services/parkingService';
import { ParkingSpace, ParkingType, ParkingAddon } from '../types/parking';
import FloorLayoutViewer from './FloorLayoutViewer';
import FabricFloorLayoutEditor from './FabricFloorLayoutEditor';
import ParkingSpaceEditModal from './ParkingSpaceEditModal';
import BulkSpaceModal from './BulkSpaceModal';
import BulkEditModal from './BulkEditModal';
import AssignParkingModal from './AssignParkingModal';
import ConfirmDialog from './ConfirmDialog';
import TypeLegend from './TypeLegend';
import { FloorSpace, SpaceUpdateData } from '../types/floorLayout';
import api from '../services/api';
import '../styles/ModelDetailsSlider.css';
import '../styles/DataTable.css';

interface ParkingFloor {
  _id: string;
  name: string;
  projectId: string;
  projectName?: string;
  layout?: {
    url: string;
    key?: string;
    width: number;
    height: number;
    size?: number;
    mimeType?: string;
  };
  spaceDefaults?: {
    width: number;
    height: number;
  };
  spaces?: {
    total?: number;
    available?: number;
    marketingHold?: number;
    salesHold?: number;
    sold?: number;
    customerSelection?: number;
    customerSubmitted?: number;
  };
  // Fallback for flat structure (if API returns it differently)
  total?: number;
  available?: number;
  marketingHold?: number;
  salesHold?: number;
  sold?: number;
  customerSelection?: number;
  customerSubmitted?: number;
}

interface ParkingFloorDetailsSliderProps {
  workspaceId: string;
  parkingFloorId: string;
  onClose: () => void;
}

type TabType = 'floor-layout' | 'parking-spaces';

const ParkingFloorDetailsSlider: React.FC<ParkingFloorDetailsSliderProps> = ({
  workspaceId,
  parkingFloorId,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('floor-layout');
  const [parkingFloor, setParkingFloor] = useState<ParkingFloor | null>(null);
  const [parkingSpaces, setParkingSpaces] = useState<ParkingSpace[]>([]);
  const [parkingTypes, setParkingTypes] = useState<ParkingType[]>([]);
  const [parkingAddons, setParkingAddons] = useState<ParkingAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSpaces, setLoadingSpaces] = useState(false);
  const [error, setError] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState<ParkingSpace | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [spaceToAssign, setSpaceToAssign] = useState<ParkingSpace | null>(null);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [spaceToRelease, setSpaceToRelease] = useState<ParkingSpace | null>(null);
  const [isReleasing, setIsReleasing] = useState(false);
  const [deletingSpaceId, setDeletingSpaceId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchParkingFloor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkingFloorId]);

  useEffect(() => {
    if (activeTab === 'parking-spaces' || activeTab === 'floor-layout') {
      fetchParkingSpaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    // Disable body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      // Re-enable body scroll when modal closes
      document.body.style.overflow = '';
    };
  }, []);

  const fetchParkingFloor = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await parkingFloorService.getById(workspaceId, parkingFloorId);
      setParkingFloor(response.parkingFloor);

      // Fetch parking types and addons for the project - await both to ensure they load before rendering
      if (response.parkingFloor.projectId) {
        await Promise.all([
          fetchParkingTypes(response.parkingFloor.projectId),
          fetchParkingAddons(response.parkingFloor.projectId)
        ]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch parking floor details');
      console.error('Error fetching parking floor:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshParkingFloorCounts = async () => {
    try {
      const response = await parkingFloorService.getById(workspaceId, parkingFloorId);
      setParkingFloor(response.parkingFloor);
    } catch (err: any) {
      console.error('Error refreshing parking floor counts:', err);
    }
  };

  const fetchParkingTypes = async (projectId: string) => {
    try {
      const response = await parkingTypeService.getAll(workspaceId, {
        projectId,
        limit: 1000
      });
      setParkingTypes(response.parkingTypes || []);
    } catch (err: any) {
      console.error('Error fetching parking types:', err);
    }
  };

  const fetchParkingAddons = async (projectId: string) => {
    try {
      const response = await parkingAddonService.getAll(workspaceId, {
        projectId,
        limit: 1000
      });
      setParkingAddons(response.parkingAddons || []);
    } catch (err: any) {
      console.error('Error fetching parking addons:', err);
    }
  };

  const fetchParkingSpaces = async () => {
    try {
      setLoadingSpaces(true);
      const params = {
        limit: 300,
        sortBy: 'numberInt',
        sortOrder: 'asc'
      };
      const response = await parkingFloorService.getSpacesByFloor(workspaceId, parkingFloorId, params);
      setParkingSpaces(response.parkingSpaces);
    } catch (err: any) {
      console.error('Error fetching parking spaces:', err);
    } finally {
      setLoadingSpaces(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return 'status-badge badge-available';
      case 'MARKETING HOLD':
        return 'status-badge badge-marketing-hold';
      case 'SALES HOLD':
        return 'status-badge badge-sales-hold';
      case 'CUSTOMER SELECTION':
        return 'status-badge badge-customer-selection';
      case 'CUSTOMER SUBMITTED':
        return 'status-badge badge-customer-submitted';
      case 'SOLD':
        return 'status-badge badge-sold';
      default:
        return 'status-badge';
    }
  };

  const handleSavePositions = async (updates: SpaceUpdateData[]) => {
    try {
      // Bulk update parking space positions in a single API call
      if (updates.length > 0) {
        await parkingFloorService.bulkUpdateSpaces(workspaceId, updates);
      }
      // Refresh spaces and parking floor data
      await fetchParkingSpaces();
      await fetchParkingFloor();
      // Don't close editor automatically after save
    } catch (err: any) {
      console.error('Error saving positions:', err);
      throw err;
    }
  };

  const handleSpaceClick = (spaceId: string) => {
    const space = parkingSpaces.find(s => s._id === spaceId);
    if (space) {
      handleEditSpace(space);
    }
  };

  const handleViewSpaceClick = useCallback((spaceId: string) => {
    const space = parkingSpaces.find(s => s._id === spaceId);
    if (space) {
      setSelectedSpace(space);
      setShowViewModal(true);
    }
  }, [parkingSpaces]);

  const handleEditSpace = (space: ParkingSpace) => {
    setSelectedSpace(space);
    setShowEditModal(true);
  };

  const handleDeleteSpace = async (spaceId: string) => {
    setDeletingSpaceId(spaceId);
    setShowEditModal(false); // Close edit modal if open
  };

  const handleCancelDelete = () => {
    setDeletingSpaceId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingSpaceId) return;

    try {
      setIsDeleting(true);
      await parkingFloorService.deleteSpace(workspaceId, deletingSpaceId);
      // Refresh both spaces and floor counts
      await Promise.all([fetchParkingSpaces(), refreshParkingFloorCounts()]);
      toast.success('Parking space deleted successfully');
      setDeletingSpaceId(null);
    } catch (err: any) {
      console.error('Error deleting space:', err);
      toast.error(err.response?.data?.message || 'Failed to delete parking space');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSpaceSaved = async () => {
    // Refresh both spaces and floor counts
    await Promise.all([fetchParkingSpaces(), refreshParkingFloorCounts()]);
    setShowEditModal(false);
  };

  const refreshAllData = async () => {
    await Promise.all([fetchParkingFloor(), fetchParkingSpaces()]);
  };

  const handleBulkSpaceSave = async (spaces: any[]) => {
    // Create all spaces using bulk create API
    await parkingFloorService.bulkCreateSpaces(workspaceId, spaces);
    await refreshAllData();
  };

  const handleBulkEdit = async (updates: any[]) => {
    try {
      // Use bulk update API instead of individual updates
      await parkingSpaceService.bulkUpdate(workspaceId, updates);
      await refreshAllData();
      toast.success('Spaces updated successfully');
    } catch (error) {
      console.error('Error bulk updating spaces:', error);
      toast.error('Failed to update spaces');
      throw error;
    }
  };

  const handleAssignClick = (space: ParkingSpace) => {
    setSpaceToAssign(space);
    setShowAssignModal(true);
  };

  const handleReleaseClick = (space: ParkingSpace) => {
    setSpaceToRelease(space);
    setShowReleaseConfirm(true);
  };

  const handleConfirmRelease = async () => {
    if (!spaceToRelease) return;

    try {
      setIsReleasing(true);
      await api.post(`/workspaces/${workspaceId}/parking-spaces/release`, {
        spaceId: spaceToRelease._id
      });
      toast.success('Parking space released successfully');
      setShowReleaseConfirm(false);
      setSpaceToRelease(null);
      // Only refresh spaces and floor counts, not the entire page
      await Promise.all([fetchParkingSpaces(), refreshParkingFloorCounts()]);
    } catch (error: any) {
      console.error('Error releasing parking space:', error);
      toast.error(error.response?.data?.message || 'Failed to release parking space');
    } finally {
      setIsReleasing(false);
    }
  };

  const handleCancelRelease = () => {
    setShowReleaseConfirm(false);
    setSpaceToRelease(null);
  };

  const handleAssignSuccess = async () => {
    // Only refresh spaces and floor counts, not the entire page
    await Promise.all([fetchParkingSpaces(), refreshParkingFloorCounts()]);
  };

  if (loading) {
    return (
      <div className="model-details-slider">
        <div className="model-details-loading">
          <div className="builders-loading-spinner" />
        </div>
      </div>
    );
  }

  if (error || !parkingFloor) {
    return (
      <div className="model-details-slider">
        <div className="model-details-error">
          <p>{error || 'Parking floor not found'}</p>
          <button className="btn btn-secondary btn-md" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="model-details-slider">
      {/* Header */}
      <div className="model-details-header">
        <div className="model-details-header-left">
          <button className="model-details-back-btn" onClick={onClose}>
            ← Back
          </button>
          <div className="model-details-title">
            <h2>Parking Floor: {parkingFloor.name}</h2>
            <p>Project: {parkingFloor.projectName || 'N/A'}</p>
          </div>
        </div>
        <button className="model-details-close-btn" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        background: '#D4CBC4',
        padding: '20px',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', gap: '8px', padding: 0 }}>
          <button
            onClick={() => setActiveTab('floor-layout')}
            style={{
              background: activeTab === 'floor-layout' ? '#48182F' : 'transparent',
              border: 'none',
              outline: 'none',
              color: activeTab === 'floor-layout' ? '#FFFFFF' : '#6B7280',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: activeTab === 'floor-layout' ? '600' : '500',
              cursor: 'pointer',
              borderRadius: '20px',
              transition: 'all 0.2s',
              boxShadow: 'none'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'floor-layout') {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                e.currentTarget.style.color = '#48182F';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'floor-layout') {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#6B7280';
              }
            }}
          >
            Floor Layout
          </button>
          <button
            onClick={() => setActiveTab('parking-spaces')}
            style={{
              background: activeTab === 'parking-spaces' ? '#48182F' : 'transparent',
              border: 'none',
              outline: 'none',
              color: activeTab === 'parking-spaces' ? '#FFFFFF' : '#6B7280',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: activeTab === 'parking-spaces' ? '600' : '500',
              cursor: 'pointer',
              borderRadius: '20px',
              transition: 'all 0.2s',
              boxShadow: 'none'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'parking-spaces') {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                e.currentTarget.style.color = '#48182F';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'parking-spaces') {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#6B7280';
              }
            }}
          >
            Parking Spaces
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="model-details-content" style={{
        gridTemplateColumns: '1fr',
        padding: '24px',
        overflow: 'auto',
        background: '#D4CBC4'
      }}>
        {activeTab === 'floor-layout' && (
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Floor Layout</h3>
              <button
                onClick={() => setShowEditor(true)}
                className="btn btn-primary btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Pencil size={16} /> Edit Floor Layout
              </button>
            </div>
            <div className="model-details-tile-content">
              {loadingSpaces ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px'
                }}>
                  <div className="builders-loading-spinner" />
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: 1 }}>
                    <FloorLayoutViewer
                      layoutImage={parkingFloor?.layout}
                      spaceDefaults={parkingFloor?.spaceDefaults}
                      spaces={parkingSpaces as FloorSpace[]}
                      parkingTypes={parkingTypes}
                      onSpaceClick={handleViewSpaceClick}
                    />
                  </div>
                  <div style={{
                    width: '280px',
                    borderLeft: '1px solid #E5E7EB',
                    paddingLeft: '20px'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '16px'
                    }}>
                      Type Legend
                    </h4>
                    <TypeLegend parkingTypes={parkingTypes} />

                    {parkingAddons.length > 0 && (
                      <>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          marginTop: '24px',
                          marginBottom: '16px'
                        }}>
                          Addon Legend
                        </h4>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px'
                        }}>
                          {parkingAddons.map((addon) => (
                            <div key={addon._id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: addon.color || '#FFA500',
                                border: '1.5px solid #ffffff',
                                boxShadow: '0 0 0 1px #D1D5DB',
                                flexShrink: 0
                              }} />
                              <span style={{ fontSize: '13px', color: '#374151' }}>
                                {addon.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'parking-spaces' && (
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <div style={{ flex: 1 }}>
                <h3>Parking Spaces</h3>
                <div style={{ fontSize: '14px', color: '#6B7280', fontWeight: '400' }}>
                  Total: {parkingFloor.spaces?.total || parkingFloor.total || 0} |
                  Available: {parkingFloor.spaces?.available || parkingFloor.available || 0} |
                  Sold: {parkingFloor.spaces?.sold || parkingFloor.sold || 0}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowBulkEditModal(true)}
                  className="btn btn-secondary btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <Edit3 size={16} /> Bulk Edit
                </button>
                <button
                  onClick={() => setShowBulkModal(true)}
                  className="btn btn-primary btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <Plus size={16} /> Bulk Add Spaces
                </button>
              </div>
            </div>
            <div className="model-details-tile-content" style={{ padding: 0 }}>
              {loadingSpaces ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div className="builders-loading-spinner" />
                </div>
              ) : parkingSpaces.length === 0 ? (
                <div className="model-details-empty">
                  <p>No parking spaces on this floor</p>
                </div>
              ) : (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Space Number</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Premium</th>
                        <th>Assigned Unit</th>
                        <th>Addons</th>
                        <th>Mandatory Addons</th>
                        <th style={{ width: '120px', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parkingSpaces.map((space, index) => (
                        <tr
                          key={space._id}
                          style={{
                            backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'
                          }}
                        >
                          <td className="data-table-primary-cell">
                            {space.number}
                          </td>
                          <td>
                            {space.type}
                          </td>
                          <td>
                            <span className={getStatusBadgeClass(space.status)}>
                              {space.status}
                            </span>
                          </td>
                          <td>
                            {space.isPremium ? (
                              <span style={{
                                color: '#48182F',
                                fontWeight: '600'
                              }}>
                                Yes
                              </span>
                            ) : (
                              <span style={{ color: '#9CA3AF' }}>No</span>
                            )}
                          </td>
                          <td>
                            {space.unitNo || '-'}
                          </td>
                          <td>
                            {space.addons && space.addons.length > 0 ? (
                              <div style={{ fontSize: '13px' }}>
                                {space.addons.map((addon, idx) => (
                                  <span key={addon._id}>
                                    {addon.name}
                                    {idx < space.addons!.length - 1 && ', '}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: '#9CA3AF' }}>-</span>
                            )}
                          </td>
                          <td>
                            {space.mandatoryAddons && space.mandatoryAddons.length > 0 ? (
                              <div style={{ fontSize: '13px' }}>
                                {space.mandatoryAddons.map((addon, idx) => (
                                  <span key={addon._id}>
                                    {addon.name}
                                    {idx < space.mandatoryAddons!.length - 1 && ', '}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: '#9CA3AF' }}>-</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                              {space.unitId ? (
                                <button
                                  onClick={() => handleReleaseClick(space)}
                                  className="btn btn-secondary btn-sm"
                                  style={{ fontSize: '12px', padding: '4px 12px' }}
                                  title="Release from unit"
                                >
                                  Release
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleAssignClick(space)}
                                  className="btn btn-primary btn-sm"
                                  style={{ fontSize: '12px', padding: '4px 12px' }}
                                  title="Assign to unit"
                                >
                                  Assign
                                </button>
                              )}
                              <button
                                onClick={() => handleEditSpace(space)}
                                className="btn btn-icon btn-sm"
                                style={{
                                  padding: '6px',
                                  color: space.status === 'SOLD' ? '#9CA3AF' : '#48182F',
                                  border: 'none',
                                  background: 'transparent',
                                  cursor: space.status === 'SOLD' ? 'not-allowed' : 'pointer',
                                  opacity: space.status === 'SOLD' ? 0.5 : 1
                                }}
                                title={space.status === 'SOLD' ? 'Cannot edit SOLD space' : 'Edit'}
                                disabled={space.status === 'SOLD'}
                              >
                                <Pencil size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteSpace(space._id)}
                                className="btn btn-icon btn-sm"
                                style={{
                                  padding: '6px',
                                  color: space.status === 'SOLD' ? '#9CA3AF' : '#DC2626',
                                  border: 'none',
                                  background: 'transparent',
                                  cursor: space.status === 'SOLD' ? 'not-allowed' : 'pointer',
                                  opacity: space.status === 'SOLD' ? 0.5 : 1
                                }}
                                title={space.status === 'SOLD' ? 'Cannot delete SOLD space' : 'Delete'}
                                disabled={space.status === 'SOLD'}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Floor Layout Editor Modal */}
      {showEditor && (
        <FabricFloorLayoutEditor
          workspaceId={workspaceId}
          floorId={parkingFloorId}
          projectId={parkingFloor?.projectId || ''}
          entityType="parking"
          floorName={parkingFloor?.name || 'Parking Floor'}
          projectName={parkingFloor?.projectName || 'Project'}
          layoutImage={parkingFloor?.layout}
          spaceDefaults={parkingFloor?.spaceDefaults || { width: 150, height: 75 }}
          parkingTypes={parkingTypes}
          parkingAddons={parkingAddons}
          spaces={parkingSpaces as FloorSpace[]}
          onClose={() => setShowEditor(false)}
          onSave={handleSavePositions}
          onSpaceClick={handleSpaceClick}
        />
      )}

      {/* Parking Space Edit Modal */}
      {showEditModal && selectedSpace && (
        <ParkingSpaceEditModal
          isOpen={showEditModal}
          workspaceId={workspaceId}
          projectId={parkingFloor?.projectId || ''}
          space={selectedSpace}
          onClose={() => {
            setShowEditModal(false);
            setSelectedSpace(null);
          }}
          onSave={handleSpaceSaved}
          onDelete={handleDeleteSpace}
        />
      )}

      {/* Bulk Space Modal */}
      {showBulkModal && (
        <BulkSpaceModal
          isOpen={showBulkModal}
          workspaceId={workspaceId}
          projectId={parkingFloor?.projectId || ''}
          floorId={parkingFloorId}
          entityType="parking"
          spaceDefaults={parkingFloor?.spaceDefaults}
          layoutDimensions={parkingFloor?.layout ? { width: parkingFloor.layout.width, height: parkingFloor.layout.height } : undefined}
          existingSpaces={parkingSpaces}
          onClose={() => setShowBulkModal(false)}
          onSave={handleBulkSpaceSave}
        />
      )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <BulkEditModal
          isOpen={showBulkEditModal}
          workspaceId={workspaceId}
          projectId={parkingFloor?.projectId || ''}
          entityType="parking"
          spaces={parkingSpaces}
          availableTypes={parkingTypes}
          availableAddons={parkingAddons}
          onClose={() => setShowBulkEditModal(false)}
          onSave={handleBulkEdit}
        />
      )}

      {/* Space Details View Modal */}
      {showViewModal && selectedSpace && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999,
            }}
            onClick={() => {
              setShowViewModal(false);
              setSelectedSpace(null);
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              zIndex: 10000,
              minWidth: '450px',
              maxWidth: '500px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#48182F' }}>
                Space Details
              </h3>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedSpace(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6B7280',
                  padding: '4px',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Space Number */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Space Number</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{selectedSpace.number}</span>
              </div>

              {/* Type */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Type</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{selectedSpace.type}</span>
              </div>

              {/* Status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Status</span>
                <span className={getStatusBadgeClass(selectedSpace.status)}>{selectedSpace.status}</span>
              </div>

              {/* Premium */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Premium</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: selectedSpace.isPremium ? '#48182F' : '#9CA3AF' }}>
                  {selectedSpace.isPremium ? 'Yes' : 'No'}
                </span>
              </div>

              {/* Assigned Unit */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Assigned Unit</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: selectedSpace.unitNo ? '#111827' : '#9CA3AF' }}>
                  {selectedSpace.unitNo || '-'}
                </span>
              </div>

              {/* Addons */}
              {selectedSpace.addons && selectedSpace.addons.length > 0 && (
                <div style={{ padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                  <span style={{ color: '#6B7280', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Add-ons</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedSpace.addons.map((addon) => (
                      <span
                        key={addon._id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: 'white',
                          borderRadius: '16px',
                          fontSize: '13px',
                          color: '#374151',
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        {addon.color && (
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: addon.color,
                            }}
                          />
                        )}
                        {addon.name}
                        {addon.mandatory && (
                          <span style={{ color: '#48182F', fontWeight: 600 }}>*</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Mandatory Addons */}
              {selectedSpace.mandatoryAddons && selectedSpace.mandatoryAddons.length > 0 && (
                <div style={{ padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '8px' }}>
                  <span style={{ color: '#92400E', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Mandatory Add-ons</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedSpace.mandatoryAddons.map((addon) => (
                      <span
                        key={addon._id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: 'white',
                          borderRadius: '16px',
                          fontSize: '13px',
                          color: '#92400E',
                          border: '1px solid #FCD34D',
                        }}
                      >
                        {addon.color && (
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: addon.color,
                            }}
                          />
                        )}
                        {addon.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedSpace(null);
                }}
                className="btn btn-secondary btn-md"
                style={{ padding: '8px 20px' }}
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deletingSpaceId && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999,
            }}
            onClick={handleCancelDelete}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              zIndex: 10000,
              minWidth: '400px',
            }}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600 }}>
              Delete Parking Space
            </h3>
            <p style={{ margin: '0 0 24px 0', color: '#6B7280', fontSize: '14px' }}>
              Are you sure you want to delete parking space{' '}
              <strong>{parkingSpaces.find(s => s._id === deletingSpaceId)?.number}</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancelDelete}
                className="btn btn-secondary btn-md"
                style={{ padding: '8px 16px' }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="btn btn-danger btn-md"
                style={{ padding: '8px 16px' }}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Assign Parking Modal */}
      {showAssignModal && spaceToAssign && (
        <AssignParkingModal
          isOpen={showAssignModal}
          workspaceId={workspaceId}
          parkingSpace={spaceToAssign}
          onClose={() => {
            setShowAssignModal(false);
            setSpaceToAssign(null);
          }}
          onSuccess={handleAssignSuccess}
        />
      )}

      {/* Release Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showReleaseConfirm}
        onClose={handleCancelRelease}
        onConfirm={handleConfirmRelease}
        title="Release Parking Space"
        message={`Are you sure you want to release this parking space from Unit ${spaceToRelease?.unitNo}?`}
        confirmText="Release"
        cancelText="Cancel"
        confirmButtonStyle="danger"
        loading={isReleasing}
      />
    </div>
  );
};

export default ParkingFloorDetailsSlider;
